import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, FormControl, FormControlLabel, FormLabel, LinearProgress, Radio, RadioGroup, Typography } from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { Sheet } from '../../components'
import { replaceShot } from './photoReviewApi'
import { checkReplaceFile, replaceErrorMessage } from './reviewModel'
import { CHECKER } from './VersionHistory'

// Shorter on a phone, so the choices below stay in reach.
const PREVIEW_HEIGHT = { xs: 168, sm: 220 }

const megabytes = bytes => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

// Replace: Daniel's hand-edited PNG (from Download, through Photoshop) becomes
// a new version of the shot, "Your edit", which still needs Approve. The
// request runs Photoroom when the shadow is added, so the sheet stays open,
// and can't be dismissed, until the server answers.
export default function ReplaceDialog({ open, slot, label, fetchResource, onClose, onReplaced }) {
    const { t } = useTranslation()
    const [file, setFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [shadow, setShadow] = useState('add')
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState(null)
    const alertRef = useRef(null)
    // A refusal lands mid-sheet: bring it into view on a short phone screen.
    useEffect(() => {
        if (error) alertRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    }, [error])

    // The chosen file on the same checkerboard as the versions, so a lost
    // cut-out or a stray background shows before it is uploaded.
    useEffect(() => {
        if (!file) { setPreviewUrl(null); return undefined }
        const url = URL.createObjectURL(file)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [file])

    function choose(event) {
        const chosen = event.target.files?.[0]
        // Cleared so picking the same file again (after a fix) still fires.
        event.target.value = ''
        if (!chosen) return
        const refusal = checkReplaceFile(chosen)
        setError(refusal)
        setFile(refusal ? null : chosen)
    }

    async function submit() {
        if (!file || uploading) return
        setUploading(true)
        setError(null)
        // Locked until the queue refresh has landed too: a second tap before
        // then would store a duplicate "Your edit".
        try {
            const response = await replaceShot(fetchResource, slot.photoId, file, shadow)
            if (response?.error) {
                setError(replaceErrorMessage(response))
                // The shot moved on (approved, or another edit landed): show it.
                if (response.status === 409) await onReplaced()
                return
            }
            await onReplaced()
            onClose()
        } finally {
            setUploading(false)
        }
    }

    return (
        <Sheet
            open={open}
            onClose={() => { if (!uploading) onClose() }}
            title={t('photoReview.replace.title', { label })}
            size='sm'
            actions={(
                <>
                    <Button disabled={uploading} onClick={onClose}>{t('photoReview.replace.cancel')}</Button>
                    <Button variant='contained' disabled={!file || uploading} onClick={submit}>{t('photoReview.replace.submit')}</Button>
                </>
            )}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                    component='label'
                    sx={{
                        display: 'block', position: 'relative', borderRadius: 1, overflow: 'hidden',
                        cursor: uploading ? 'default' : 'pointer',
                        border: 1, borderStyle: file ? 'solid' : 'dashed', borderColor: 'divider',
                        '&:hover': uploading ? undefined : { borderColor: 'primary.main' },
                        '&:focus-within': { outline: theme => `3px solid ${theme.palette.primary.dark}`, outlineOffset: 2 },
                    }}
                >
                    <Box
                        component='input'
                        type='file'
                        accept='image/png,.png'
                        aria-label={t('photoReview.replace.choose')}
                        disabled={uploading}
                        onChange={choose}
                        sx={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }}
                    />
                    {previewUrl ? (
                        <Box sx={{ height: PREVIEW_HEIGHT, ...CHECKER }}>
                            <img src={previewUrl} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                        </Box>
                    ) : (
                        <Box sx={{ height: PREVIEW_HEIGHT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2, textAlign: 'center', bgcolor: 'action.hover' }}>
                            <UploadFileOutlinedIcon color='action' fontSize='large' />
                            <Typography sx={{ fontWeight: 600 }} color='primary'>{t('photoReview.replace.choose')}</Typography>
                            <Typography variant='caption' color='text.secondary'>{t('photoReview.replace.limits')}</Typography>
                        </Box>
                    )}
                </Box>
                {file && (
                    <Box sx={{ mt: -1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                            <Typography variant='body2' sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                            <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>{megabytes(file.size)}</Typography>
                        </Box>
                        <Typography variant='caption' color='text.secondary'>{t('photoReview.replace.chooseAnother')}</Typography>
                    </Box>
                )}

                {/* Next to the file it is about, so a phone sees it without scrolling. */}
                {uploading && (
                    <Box>
                        <LinearProgress aria-label={t('photoReview.replace.uploading')} />
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>
                            {t(shadow === 'add' ? 'photoReview.replace.uploadingShadow' : 'photoReview.replace.uploading')}
                        </Typography>
                    </Box>
                )}
                {error && <Alert ref={alertRef} severity='error'>{t(error.key, error.values)}</Alert>}

                <FormControl disabled={uploading}>
                    <FormLabel id='replace-shadow' sx={{ fontWeight: 600, color: 'text.primary', '&.Mui-focused': { color: 'text.primary' } }}>
                        {t('photoReview.replace.shadow')}
                    </FormLabel>
                    <RadioGroup aria-labelledby='replace-shadow' value={shadow} onChange={event => setShadow(event.target.value)}>
                        {['add', 'none'].map(value => (
                            <FormControlLabel
                                key={value}
                                value={value}
                                control={<Radio size='small' />}
                                sx={{ alignItems: 'flex-start', mt: 1, '& .MuiRadio-root': { pt: 0.25 } }}
                                label={(
                                    <>
                                        <Typography variant='body2' sx={{ fontWeight: 600 }}>{t(`photoReview.replace.shadowOptions.${value}`)}</Typography>
                                        <Typography variant='caption' color='text.secondary' component='p'>{t(`photoReview.replace.shadowHints.${value}`)}</Typography>
                                    </>
                                )}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>

                <Typography variant='body2' color='text.secondary'>{t('photoReview.replace.note')}</Typography>
            </Box>
        </Sheet>
    )
}
